import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Form,
  Link,
  Modal,
  Loading,
} from '@puppet/react-components';
import CommonForm from '@components/CommonForm';
import Definitions from '@components/Definitions';
import { Trans, useTranslation } from 'react-i18next';
import { StdVcsSource } from '@utils/api/cd4pe/vcs/actions';
import * as ERRORS from '@utils/boltProjects/errorTypesConsts';
import actionCreator, { Actions } from '@utils/actionCreator';
import { CommonFormChangeFunction } from '@components/CommonForm/CommonForm';
import { Cd4peApiError } from '@utils/api/cd4pe';
import Cd4peError from '@components/Cd4peError';
import { LINKS } from 'src/routes';
import docsLinks from '@codeDelivery/utils/docsLinks';
import GenericErrorAlert from '../GenericErrorAlert';

interface EditCodeProjectFormActionTypes {
  SELECTED_SOURCE: 'SELECTED_SOURCE';
  NAME: 'NAME';
}

export const EDIT_CODE_PROJECT_FORM_ACTION_TYPES: EditCodeProjectFormActionTypes =
  {
    SELECTED_SOURCE: 'SELECTED_SOURCE',
    NAME: 'NAME',
  };

export const editCodeProjectFormActions = {
  name: (name: string) =>
    actionCreator(EDIT_CODE_PROJECT_FORM_ACTION_TYPES.NAME, name),
  selectedSource: (source: string) =>
    actionCreator(EDIT_CODE_PROJECT_FORM_ACTION_TYPES.SELECTED_SOURCE, source),
};

export type EditCodeProjectFormActions = Actions<
  typeof editCodeProjectFormActions
>;

type FormFieldValues = {
  [K in keyof EditCodeProjectFormActionTypes]: string | null | undefined;
};

interface StdItem<K, T> {
  value: K;
  actualValue: T;
}

export const getActualValue = <
  ItemType extends StdItem<ValueType, ActualValueType>,
  ValueType = ItemType['value'],
  ActualValueType = ItemType['actualValue'],
>(
  items: ItemType[],
  selectedItem: ItemType['value'],
) => {
  const item = items.find((i) => {
    return i.value === selectedItem;
  });
  if (item) {
    return item.actualValue;
  }
  return null;
};

export type EditCodeProjectFormOnChange =
  CommonFormChangeFunction<FormFieldValues>;

interface Props {
  workspaceName: string;
  projectType: 'module' | 'controlRepo';
  sources?: StdVcsSource[] | null;
  selectedSource?: string | null;
  getSourcesLoading?: boolean | null;
  getSourcesError?: string | null;
  getReposLoading?: boolean | null;
  getReposError?: string | null;
  name?: string | null;
  saveLoading?: boolean;
  saveError?: Cd4peApiError | string | null;
  onFormChange: EditCodeProjectFormOnChange;
  submitCallback: () => Promise<void>;
  submitDisabled: boolean;
  cancelCallback: () => void;
  webhookError?: boolean;
  closeModal: () => void;
}

const EditCodeProjectForm = ({
  sources,
  selectedSource,
  getSourcesLoading,
  getSourcesError,
  getReposLoading,
  getReposError,
  name,
  saveLoading,
  saveError,
  webhookError,
  workspaceName,
  projectType,
  onFormChange,
  submitCallback,
  submitDisabled,
  cancelCallback,
  closeModal,
}: Props) => {
  const { t } = useTranslation('codeDelivery');

  const formVals = {
    SELECTED_SOURCE: selectedSource,
    NAME: name,
  };

  const renderLoader = (condition?: boolean | null) => {
    return condition ? (
      <span className="edit-code-project-field__icon-wrapper">
        <Loading
          data-testid="edit-code-project-loader"
          className="edit-code-project-field__loader-icon"
        />
      </span>
    ) : null;
  };

  const renderSourcesAlert = () => {
    if (getSourcesError === ERRORS.NO_SOURCES_FOUND) {
      return (
        <Alert type="warning" className="edit-code-project__generic-error">
          {t('addCodeProject.sourceSection.noSourcesFound.title')}
          <Alert.Message>
            <Trans
              t={t}
              i18nKey={`addCodeProject.sourceSection.noSourcesFound.body.${projectType}`}
              components={[
                <Link
                  as={NavLink}
                  to={LINKS.settings.listSourceControl({
                    path: { workspace: workspaceName },
                  })}
                  size="small"
                />,
              ]}
            />
          </Alert.Message>
        </Alert>
      );
    }

    if (getSourcesError) {
      return (
        <GenericErrorAlert
          title={t('addCodeProject.sourceSection.sourcesGenericError')}
          description={getSourcesError}
          className="edit-code-project__generic-error"
        />
      );
    }
    return null;
  };

  const renderReposError = () => {
    if (getReposError === ERRORS.NO_REPOS_FOUND) {
      return (
        <div className="add-code-project-field__wrapper">
          <Alert type="warning">
            {t('addCodeProject.sourceSection.noReposFound')}
            <Alert.Message>
              {t(
                `addCodeProject.sourceSection.noReposFoundDescription.${projectType}`,
              )}
            </Alert.Message>
          </Alert>
        </div>
      );
    }

    if (getReposError) {
      return (
        <div className="add-code-project-field__wrapper">
          <GenericErrorAlert
            title={t('addCodeProject.sourceSection.reposGenericError')}
            description={getReposError}
          />
        </div>
      );
    }

    return null;
  };

  const renderWebhookModal = () => {
    if (webhookError) {
      return (
        <Modal
          className="edit-code-project-modal"
          onClose={closeModal}
          closeOnEscapeAndOverlay={false}
        >
          <Modal.Title>
            {t(`editCodeProject.webhookErrorModal.title.${projectType}`)}
          </Modal.Title>
          <Alert type="warning">
            {t(`editCodeProject.webhookErrorModal.warning`)}
          </Alert>
          {t(`editCodeProject.webhookErrorModal.text.${projectType}`)}
          <Modal.Actions>
            <Button
              as="a"
              href={docsLinks().webhookTroubleshooting}
              icon="book"
            >
              {t(`editCodeProject.webhookErrorModal.docsButton`)}
            </Button>
            <Button type="secondary" onClick={closeModal}>
              {t(`editCodeProject.webhookErrorModal.goToButton.${projectType}`)}
            </Button>
          </Modal.Actions>
        </Modal>
      );
    }
    return null;
  };

  return (
    <CommonForm
      submittable
      cancellable
      className="edit-code-project"
      submitLabel={t(`editCodeProject.form.submitButton.${projectType}`)}
      submitDisabled={submitDisabled}
      onSubmit={submitCallback}
      onCancel={cancelCallback}
      values={formVals}
      error={typeof saveError === 'string' ? saveError : null}
      onChange={onFormChange}
      submitting={saveLoading}
      data-testid="edit-code-project-form"
    >
      {renderSourcesAlert()}
      {renderReposError()}
      <CommonForm.Section>
        <CommonForm.Section.Main>
          <h2 className="edit-code-project-section__header">
            {t('codeProject.sourceSection.edit.header')}
          </h2>
          <div
            className="edit-code-project-field__wrapper"
            data-testid="edit-code-project-source-select"
          >
            <Form.Field
              type="select"
              name={EDIT_CODE_PROJECT_FORM_ACTION_TYPES.SELECTED_SOURCE}
              label={t('addCodeProject.sourceSection.sourceLabel')}
              placeholder={t('addCodeProject.sourceSection.sourcePlaceholder')}
              required
              options={sources}
              disabled={!sources || sources.length === 0}
            />
            {renderLoader(getSourcesLoading)}
            {renderLoader(getReposLoading)}
          </div>
        </CommonForm.Section.Main>
        <CommonForm.Section.Sidebar>
          <Definitions.Entry icon="info-circle">
            <Definitions.Entry.Content>
              <Trans
                t={t}
                i18nKey="addCodeProject.sourceSection.rightboundText"
                components={[
                  <Link
                    as={NavLink}
                    to={LINKS.settings.listSourceControl({
                      path: { workspace: workspaceName },
                    })}
                    size="small"
                  />,
                ]}
              />
            </Definitions.Entry.Content>
          </Definitions.Entry>
        </CommonForm.Section.Sidebar>
      </CommonForm.Section>

      {renderWebhookModal()}
      <Cd4peError error={typeof saveError === 'string' ? null : saveError} />
    </CommonForm>
  );
};

export default EditCodeProjectForm;

EditCodeProjectForm.defaultProps = {
  sources: null,
  selectedSource: null,
  getSourcesLoading: false,
  getSourcesError: null,
  getReposLoading: null,
  getReposError: null,
  repoFilterCallback: null,
  name: null,
  saveLoading: false,
  saveError: null,
  formError: null,
  webhookError: false,
  submitDisabled: false,
} as Partial<Props>;
